import { createSelector } from 'reselect';
import { numeric, ROOT_SECTION, ROOT_SOURCE, sortBy, VERSION } from 'shared-base';
import { defaultWackValues, wackMap } from '../data/data.wack';
import { parsePlan } from '../utils/plan';
import { raw } from './selectors.raw';
import * as single from './selectors.single';
import { calculateMeta } from './utils/generators';

const ROOT = ROOT_SECTION('generators');

export const $projects = createSelector(raw.$rawProjects, raw.$rawCurrentIds, (projects) => {
  return Object.values(projects).map((project) => {
    const { models = {} } = project;

    const modelsCount = Object.keys(models).length;

    return {
      ...project,
      modelsCount,
    };
  });
});

export const $playground = createSelector($projects, (projects) => {
  return projects.find((project: IProject) => project.type === 'playground');
});

export const $goldens = createSelector(raw.$rawGoldens, (goldens) => {
  return Object.values(goldens).sort(sortBy(['generatorId', 'createdAt'], 'asc'));
});

export const $qualities = createSelector(raw.$rawQualities, (qualities) => {
  return Object.values(qualities).sort(sortBy(['service', 'createdAt'], 'asc'));
});

export const $subtitles = createSelector(raw.$rawRealtimeState, (realtimeState) => {
  const { subtitles, subtitlesTs, isEnabled } = realtimeState;
  const delta = Date.now() - subtitlesTs;
  const isExpired = delta > 1000 * 3;

  if (!isEnabled || isExpired) {
    return '';
  }

  return subtitles;
});

export const $editorCommands = createSelector(raw.$rawEditorCommands, (commands) => {
  return Object.values(commands)
    .sort(sortBy(['ts'], 'asc'))
    .map((command) => {
      const { tsEnd } = command;
      let isArchived = false;

      if (tsEnd) {
        const delta = Date.now() - tsEnd;
        isArchived = delta > 1000 * 3;
      }

      return {
        ...command,
        isArchived,
      };
    });
});

export const $pendingEditorCommands = createSelector($editorCommands, (commands) => {
  return commands.filter((command) => {
    const { status } = command;
    return status === 'pending';
  });
});

export const $user = createSelector(raw.$rawUser, (user) => {
  const parsedPlan = parsePlan(user);
  const isVisitor = parsedPlan.planId === 'visitor';

  return {
    ...user,
    ...parsedPlan,
    isVisitor,
  };
});

export const $isVisitor = createSelector($user, (user) => {
  return user.planId === 'visitor';
});

export const $isPlayground = createSelector(
  $playground,
  raw.$rawCurrentIds,
  (playground, currentIds) => {
    const { projectId } = currentIds;

    return playground && playground.id === projectId;
  }
);

export const $isSubscriber = createSelector(raw.$rawUser, (user) => {
  const { planId } = user;
  return planId.startsWith('pro');
});

export const $isEmployee = createSelector(raw.$rawUser, (user) => {
  const { email, isReviewer } = user ?? {};
  return email.endsWith('@sloyd.ai') || isReviewer;
});

export const $generators = createSelector(
  raw.$rawGenerators,
  raw.$rawSettings,

  (generators, settings) => {
    const { whiteThumbnails } = settings;

    return Object.values(generators)
      .filter((generator) => generator.visibility === 'public')
      .map((generator) => {
        const imageUrl = whiteThumbnails
          ? `${ROOT_SOURCE}/${generator.id}.png?${VERSION}`
          : `${ROOT}/${generator.id}-300.png?${VERSION}`;

        return {
          ...generator,
          meta: calculateMeta(generator),
          thumbUrl: imageUrl,
          imageUrl: imageUrl,
          white: whiteThumbnails,
        } as IGenerator;
      });
  }
);

export const $materials = createSelector(raw.$rawAllMaterials, (materials) => {
  return Object.values(materials ?? {}) as IMaterial[];
});

export const $backdrops = createSelector(raw.$rawAllBackdrops, (backdrops) => {
  return Object.values(backdrops) as IShader[];
});

export const $shaders = createSelector(raw.$rawAllShaders, (shaders) => {
  return Object.values(shaders) as IShader[];
});

export const $assetPacks = createSelector(raw.$rawAssetPacks, (assetPacks) => {
  return Object.values(assetPacks) as IAssetPack[];
});

export const $forContext = createSelector(
  raw.$rawAppState,
  $user,
  raw.$rawPalettes,
  raw.$rawUserPalettes,
  raw.$rawAssetPacks,
  raw.$rawGenerators,
  raw.$rawProjects,
  (appState, user, palettes, userPalettes, assetPacks, generators, projects) => {
    return {
      appState,
      sloydUser: user,
      allPalettes: Object.values(palettes),
      userPalettes: Object.values(userPalettes),
      carousels: Object.values(assetPacks),
      generators: Object.values(generators),
      projects: Object.values(projects),
      popularTags: [
        'building',
        'furniture',
        'urban',
        'medieval',
        'household',
        'weapon',
        'outdoor',
        'kitchen',
      ],
    };
  }
);

export const $logs = createSelector(raw.$rawLogs, (logs) => {
  return Object.values(logs).sort(sortBy('timestamp', 'desc'));
});

export const $palettes = createSelector(raw.$rawPalettes, (palettes) => {
  return Object.values(palettes ?? {});
});

export const $models = createSelector(raw.$rawModels, raw.$rawCurrentIds, (models, currentIds) => {
  const { randomId } = currentIds;
  if (randomId) {
  }

  return Object.values(models).sort(sortBy('createdAt', 'desc'));
});

export const $breadcrumbs = createSelector(
  single.$currentProject,
  single.$currentModel,
  (project, model) => {
    const output: Json[] = [];

    output.push({
      id: 'projects',
      title: 'Projects',
      path: '/projects',
    });

    if (project) {
      output.push({
        id: project.id,
        title: project.name,
        path: `/editor/${project.id}`,
      });

      if (model) {
        output.push({
          id: model.id,
          title: model.name,
          path: `/editor/${project.id}/${model.id}`,
        });
      }
    }

    return output;
  }
);

export const $currentBarsIndex = createSelector(raw.$rawAppState, (appState) => {
  const { path = '' } = appState;

  const isEditor = /editor/i.test(path);
  const isModel = /editor\/[a-z-0-9]+\/[a-z-0-9]+/i.test(path);

  let topBarIndex = 0;
  let navBarIndex = 0;

  if (isEditor) {
    navBarIndex = 1;
    topBarIndex = 1;
  }

  return {
    navBarIndex,
    topBarIndex,
  };
});

export const $isCurrentModelEmpty = createSelector(single.$currentModel, (model) => {
  if (!model) return true;

  const { generatorId } = model;
  return !generatorId;
});

export const $focusMode = createSelector(
  single.$currentModel,
  raw.$rawCurrentIds,
  (model, currentIds) => {
    const { modelId } = currentIds;

    if (modelId === 'new-model') {
      return 'spawn';
    } else if (modelId && model) {
      return model.generatorId ? 'existing' : 'spawn';
    } else {
      return 'none';
    }
  }
);

export const $isLoading = createSelector(raw.$rawAppState, (appState) => {
  const { isLoading, isLoadingFresh } = appState;

  return isLoading || isLoadingFresh;
});

export const $editMode = createSelector(raw.$rawAppState, (appState) => {
  const { editMode } = appState;
  return editMode;
});

export const $nextPrevModels = createSelector($models, raw.$rawCurrentIds, (models, currentIds) => {
  const { projectId, modelId } = currentIds;

  const output = {
    nextExists: false,
    nextId: '',
    nextPath: '',
    currentId: modelId,
    prevExists: false,
    prevId: '',
    prevPath: '',
  };

  const modelIndex = models.findIndex((model) => model.id === modelId);

  const nextModel = models[modelIndex + 1];
  const prevModel = models[modelIndex - 1];

  if (nextModel) {
    output.nextExists = true;
    output.nextId = nextModel.id;
    output.nextPath = `/editor/${projectId}/${nextModel.id}`;
  }

  if (prevModel) {
    output.prevExists = true;
    output.prevId = prevModel.id;
    output.prevPath = `/editor/${projectId}/${prevModel.id}`;
  }

  return output;
});

export const $keyframes = createSelector(raw.$rawKeyframes, (keyframes) => {
  return Object.values(keyframes)
    .map((keyframe) => {
      return {
        ...keyframe,
        timestampPercent: keyframe.ts / 15,
        duration: 1,
      };
    })
    .sort(sortBy('ts'));
});

export const $storyLines = createSelector(
  raw.$rawStoryLines,
  raw.$rawAnimationState,
  (lines, animationState) => {
    return Object.values(lines).map((line, index) => {
      return {
        ...line,
        isSelected: animationState.storyLineIndex === index,
      };
    });
  }
);

export const $showLoaderBar = createSelector(raw.$rawAppState, (appState) => {
  const { isAiRunning, isDataReady, isLoading } = appState;
  return isLoading || isAiRunning || !isDataReady;
});

export const $audioKeyframes = createSelector($keyframes, (keyframes) => {
  return keyframes.filter((k) => k.type === 'audio');
});

export const $sfxKeyframes = createSelector($keyframes, (keyframes) => {
  return keyframes.filter((k) => k.type === 'sfx');
});

export const $cameraKeyframes = createSelector($keyframes, (keyframes) => {
  return keyframes.filter((k) => k.type === 'camera');
});

export const $currentKeyframes = createSelector(
  [raw.$rawKeyframes, (_state, ts) => ts],
  (keyframes, ts) => {
    return Object.values(keyframes).filter((k) => Math.abs(k.ts - ts) < 0.3);
  }
);

export const $credits = createSelector($user, (user) => {
  return user.credits;
});

export const $account = createSelector($user, (user) => {
  const { planId, planName, credits, nextCreditUpdateDate, clientId, clientSecret } = user;

  return {
    plan: {
      planName,
      creditsLeft: credits,
      creditsRenewal: nextCreditUpdateDate,
    },
    api: {
      clientId,
      clientSecret,
    },
  };
});

export const $wacom = createSelector(
  raw.$rawValuesModel,
  raw.$rawCurrentIds,
  (valuesModel, currentIds) => {
    const output: any = {
      x: 0,
      y: 0,
    };

    const { wackModeId: mode } = currentIds;

    const map = wackMap[mode];
    const { xKey, yKey } = map;

    let x = valuesModel[xKey] || defaultWackValues[xKey];
    let y = valuesModel[yKey] || defaultWackValues[yKey];

    output.x = x;
    output.y = y;

    return output;
  }
);

export const $isSlimVersion = createSelector(raw.$rawAppState, (appState) => {
  return appState.isSlimVersion;
});

export const $holodeck = createSelector(
  raw.$rawHolodeckState,
  raw.$rawHolodeckStateForce,
  (holodeck, holdeckForce) => {
    return {
      ...holodeck,
      ...holdeckForce,
    };
  }
);

export const $sceneItems = createSelector(raw.$rawSceneItems, (items) => {
  return Object.values(items);
});

export const $characters = createSelector(raw.$rawCharacters, (characters) => {
  return Object.values(characters);
});

export const $permutationsCount = createSelector(
  raw.$rawGenerators,
  raw.$rawAllMaterials,
  (generators, materials) => {
    const params = Object.values(generators).reduce((acc, generator) => {
      const { parameters = [] } = generator;

      const permutations = parameters.reduce((acc2, param) => {
        const { type, options = [] } = param;

        switch (type) {
          case 'picker':
            return acc2 * (options.length ?? 1);
          case 'slider':
            return acc2 * 10;
          case 'toggle':
            return acc2 * 2;
        }
      }, 1);

      return acc + permutations;
    }, 0);

    const mat = Object.keys(materials).length;

    return numeric(params * mat * 4, 'exponantial');
  }
);

export const $goldensByGenerator = createSelector($goldens, (goldens) => {
  const output: Record<string, IGolden[]> = {};

  goldens.forEach((g) => {
    const { generatorId } = g;
    output[generatorId] = output[generatorId] || [];
    output[generatorId].push(g);
  });

  return output;
});

export const $generatorsWithGoldens = createSelector(
  $generators,
  $goldensByGenerator,
  (generators, goldensByGenerator) => {
    return generators.map((g) => {
      const { id } = g;
      const goldens = goldensByGenerator[id] || [];

      return {
        ...g,
        goldens,
      } as IGenerator;
    });
  }
);

export const $adminGoldensStats = createSelector($generatorsWithGoldens, (generators) => {
  const stats: any = {
    total: 0,
    emptyCount: 0,
    existsCount: 0,
  };

  generators.forEach((g) => {
    const { goldens = [] } = g;
    stats.total += goldens.length;

    if (goldens.length === 0) {
      stats.emptyCount += 1;
    } else {
      stats.existsCount += 1;
    }
  });

  stats.neededBrowse = generators.length;
  stats.percentBrowse = Math.round((stats.existsCount / generators.length) * 100);
  stats.neededExamples = 4 * generators.length;
  stats.percentExamples = Math.round((100 * stats.total) / (4 * generators.length));

  return [
    {
      label: `Browse Ideas (${numeric(stats.neededBrowse)} needed)`,
      stats: numeric(stats.percentBrowse) + '%',
      progress: stats.percentBrowse,
      color: 'violet.6',
      icon: 'IconArrowUpRight',
    },
    {
      label: `Examples (${numeric(stats.neededExamples)} needed)`,
      stats: numeric(stats.percentExamples) + '%',
      progress: stats.percentExamples,
      color: 'blue.6',
      icon: 'IconArrowUpRight',
    },
    {
      label: 'Goldens',
      stats: numeric(stats.total),
      progress: stats.percentBrowse,
      color: 'green.6',
      icon: 'IconArrowUpRight',
    },
  ] as const;
});
